import React, { useMemo } from 'react';
import PdfSection from 'Views/Pages/Pdf/Components/PDFSection';
import { capitalize, uniqBy } from 'lodash';
import PdfCourseAccordion from 'Views/Pages/Pdf/Components/PDFCourseAccordion';
import { CourseEntity, StudyPlanEntity } from 'Models/Entities';
import PdfCourseAccordionItem from 'Views/Pages/Pdf/Components/PDFCourseAccordionItem';
import PdfChipGroup from 'Views/Pages/Pdf/Chip/PDFChipGroup';
import PdfChip from 'Views/Pages/Pdf/Chip/PdfChip';
import { DataIndex } from 'Util/DataIndexUtils';
import { sumBy } from 'lodash';

interface UnitCompletedProps {
	studyPlan: StudyPlanEntity,
	coursesById: DataIndex<CourseEntity>,
	completedCourseIds: string[],
	getCourseComponents: (courseId: string) => string[],
	totalUnitsSelected: number,
}

const UnitCompleted = (props: UnitCompletedProps) => {
	const {
		studyPlan,
		coursesById,
		completedCourseIds,
		getCourseComponents,
		totalUnitsSelected,
	} = props;

	const distinctCompletedPlanSelections = uniqBy(
		(studyPlan?.getCompletedPlanSelections() || []),
		planSelection => planSelection.courseId,
	);

	// map the distinct completed courses to their course entities
	// to provide to the completed course accordion
	const distinctCompletedCourses = distinctCompletedPlanSelections
		.map(selection => coursesById[selection.courseId])
		.sort((a, b) => a.code.localeCompare(b.code));

	const completedUnits = sumBy(distinctCompletedCourses, course => course.units);

	return (
		<PdfSection
			title={`${capitalize(window.lexicon.units)} completed`}
			unitCount={`${completedUnits}/${totalUnitsSelected}`}
		>
			<PdfCourseAccordion<CourseEntity>
				title={`Completed ${window.lexicon.courses}`}
				hideTitle
				emptyLabel={`No completed ${window.lexicon.courses}`}
				items={distinctCompletedCourses}
				renderItem={(course, index) => {
					const courseComponents = getCourseComponents(course.id);

					return (
						<PdfCourseAccordionItem
							course={course}
							showRequisiteDescription={false}
							label={(
								<PdfChipGroup>
									{
										courseComponents.map(component => (
											<PdfChip label={component} />
										))
									}
								</PdfChipGroup>
							)}
							additionalStatus={
								completedCourseIds.includes(course.id)
									? (
										<PdfChipGroup>
											<PdfChip label={capitalize(window.lexicon.completedCredit)} isSuccess />
										</PdfChipGroup>
									)
									: null
							}
						/>
					);
				}}
			/>
		</PdfSection>
	);
};

export default UnitCompleted;
